import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Key, Copy, Eye, EyeOff, FileText } from 'lucide-react';

const TenantManager = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    openai_api_key: ''
  });
  const [showApiKeys, setShowApiKeys] = useState({});
  const [showOpenAIKeys, setShowOpenAIKeys] = useState({});

  // Prompt editing state
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [editingPromptTenant, setEditingPromptTenant] = useState(null);
  const [promptText, setPromptText] = useState('');
  const [fallbackPromptText, setFallbackPromptText] = useState('');
  const [categorizationPromptText, setCategorizationPromptText] = useState('');
  const [enableInlineDocumentLinks, setEnableInlineDocumentLinks] = useState(false);
  const [sourceMode, setSourceMode] = useState('retrieval_based');
  const [sourceMatchMode, setSourceMatchMode] = useState('fuzzy');
  const [maxSources, setMaxSources] = useState(4);
  const [searchSemanticWeight, setSearchSemanticWeight] = useState(0.62);
  const [searchKeywordWeight, setSearchKeywordWeight] = useState(0.25);
  const [searchTitleWeight, setSearchTitleWeight] = useState(0.13);
  const [savingPrompt, setSavingPrompt] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/tenant/');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTenant) {
        // For editing, use the update endpoint
        await axios.put(`/api/tenant/${editingTenant.id}`, formData);
        toast.success('Tenant updated successfully');
      } else {
        // For creating, we use the register endpoint
        await axios.post('/api/tenant/register', formData);
        toast.success('Tenant created successfully');
      }
      
      setShowModal(false);
      setEditingTenant(null);
      setFormData({ name: '', slug: '', openai_api_key: '' });
      fetchTenants();
    } catch (error) {
      console.error('Error saving tenant:', error);
      toast.error('Failed to save tenant');
    }
  };

  const handleToggleStatus = async (tenantId) => {
    try {
      await axios.put(`/api/tenant/${tenantId}/toggle`);
      toast.success('Tenant status updated');
      fetchTenants();
    } catch (error) {
      console.error('Error toggling tenant status:', error);
      toast.error('Failed to update tenant status');
    }
  };

  const handleRegenerateApiKey = async (tenantId) => {
    try {
      await axios.post(`/api/tenant/${tenantId}/regenerate-api-key`);
      toast.success('API key regenerated successfully');
      fetchTenants();
    } catch (error) {
      console.error('Error regenerating API key:', error);
      toast.error('Failed to regenerate API key');
    }
  };

  const handleDeleteTenant = async (tenantId, tenantName) => {
    if (!window.confirm(`Are you sure you want to delete tenant "${tenantName}"? This action cannot be undone and will delete all associated data (FAQs, documents, chat history, etc.).`)) {
      return;
    }

    try {
      await axios.delete(`/api/tenant/${tenantId}`);
      toast.success('Tenant deleted successfully');
      fetchTenants();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    }
  };



  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleApiKeyVisibility = (tenantId) => {
    setShowApiKeys(prev => ({
      ...prev,
      [tenantId]: !prev[tenantId]
    }));
  };

  const toggleOpenAIKeyVisibility = (tenantId) => {
    setShowOpenAIKeys(prev => ({
      ...prev,
      [tenantId]: !prev[tenantId]
    }));
  };

  const openCreateModal = () => {
    setEditingTenant(null);
    setFormData({ name: '', slug: '', openai_api_key: '' });
    setShowModal(true);
  };

  const handleEditPrompt = async (tenant) => {
    try {
      const token = localStorage.getItem('token');
      const data = await axios.get(`/api/admin/prompts/${tenant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingPromptTenant(tenant);
      setPromptText(data.data.manual_prompt || '');
      setFallbackPromptText(data.data.fallback_prompt || '');
      setCategorizationPromptText(data.data.categorization_prompt || '');
      setEnableInlineDocumentLinks(!!data.data.enable_inline_document_links);
      setSourceMode(data.data.source_mode || 'retrieval_based');
      setSourceMatchMode(data.data.source_match_mode || 'fuzzy');
      setMaxSources(data.data.max_sources || 4);
      setSearchSemanticWeight(data.data.search_semantic_weight ?? 0.62);
      setSearchKeywordWeight(data.data.search_keyword_weight ?? 0.25);
      setSearchTitleWeight(data.data.search_title_weight ?? 0.13);
      setShowPromptModal(true);
    } catch (error) {
      console.error('Error loading prompt:', error);
      toast.error('Failed to load conversation prompt');
    }
  };

  const handleSavePrompt = async () => {
    if (!editingPromptTenant) return;

    // Validate search weights before sending
    const sem = Number(searchSemanticWeight);
    const kw  = Number(searchKeywordWeight);
    const ttl = Number(searchTitleWeight);
    if ([sem, kw, ttl].some(v => isNaN(v) || v < 0 || v > 1)) {
      toast.error('Each search weight must be between 0.00 and 1.00');
      return;
    }
    if (sem + kw + ttl === 0) {
      toast.error('At least one search weight must be greater than 0');
      return;
    }

    setSavingPrompt(true);
    try {
      const normalizedMaxSources = Math.min(10, Math.max(1, Number(maxSources) || 4));
      const token = localStorage.getItem('token');
      await axios.put(`/api/admin/prompts/${editingPromptTenant.id}`, {
        manual_prompt: promptText,
        fallback_prompt: fallbackPromptText,
        categorization_prompt: categorizationPromptText,
        enable_inline_document_links: enableInlineDocumentLinks,
        source_mode: sourceMode,
        source_match_mode: sourceMatchMode,
        max_sources: normalizedMaxSources,
        search_semantic_weight: Number(searchSemanticWeight),
        search_keyword_weight: Number(searchKeywordWeight),
        search_title_weight: Number(searchTitleWeight),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conversation prompt saved successfully!');
      setShowPromptModal(false);
      setEditingPromptTenant(null);
      setPromptText('');
      setFallbackPromptText('');
      setCategorizationPromptText('');
      setEnableInlineDocumentLinks(false);
      setSourceMode('retrieval_based');
      setSourceMatchMode('fuzzy');
      setMaxSources(4);
      setSearchSemanticWeight(0.62);
      setSearchKeywordWeight(0.25);
      setSearchTitleWeight(0.13);
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error('Failed to save conversation prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div>Loading tenants...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="page-title">Tenant Manager</h1>
            <p className="page-subtitle">Manage your chatbot tenants</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={16} style={{ marginRight: '8px' }} />
            Add Tenant
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Tenants</h3>
        </div>
        
        {tenants.length === 0 ? (
          <div className="empty-state">
            <h3>No tenants found</h3>
            <p>Create your first tenant to get started.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>API Key</th>
                  <th>OpenAI API Key</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr key={tenant.id}>
                    <td>{tenant.name}</td>
                    <td>
                      <code>{tenant.slug}</code>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <code style={{ 
                          fontFamily: 'monospace',
                          backgroundColor: '#f8f9fa',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          marginRight: '8px'
                        }}>
                          {showApiKeys[tenant.id] 
                            ? tenant.api_key 
                            : tenant.api_key.substring(0, 8) + '...'
                          }
                        </code>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => toggleApiKeyVisibility(tenant.id)}
                          title={showApiKeys[tenant.id] ? 'Hide' : 'Show'}
                        >
                          {showApiKeys[tenant.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => copyToClipboard(tenant.api_key)}
                          title="Copy"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <code style={{ 
                          fontFamily: 'monospace',
                          backgroundColor: '#f8f9fa',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          marginRight: '8px'
                        }}>
                          {showOpenAIKeys[tenant.id] 
                            ? tenant.openai_api_key 
                            : tenant.openai_api_key.substring(0, 8) + '...'
                          }
                        </code>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => toggleOpenAIKeyVisibility(tenant.id)}
                          title={showOpenAIKeys[tenant.id] ? 'Hide' : 'Show'}
                        >
                          {showOpenAIKeys[tenant.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => copyToClipboard(tenant.openai_api_key)}
                          title="Copy"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${tenant.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {tenant.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleToggleStatus(tenant.id)}
                          title={tenant.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {tenant.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleEditPrompt(tenant)}
                          title="Edit Conversation Prompt"
                        >
                          <FileText size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleRegenerateApiKey(tenant.id)}
                          title="Regenerate API Key"
                        >
                          <Key size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setEditingTenant(tenant);
                            setFormData({
                              name: tenant.name,
                              slug: tenant.slug,
                              openai_api_key: tenant.openai_api_key || ''
                            });
                            setShowModal(true);
                          }}
                          title="Edit Tenant"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteTenant(tenant.id, tenant.name)}
                          title="Delete Tenant"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingTenant ? 'Edit Tenant' : 'Create New Tenant'}
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Slug</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                  placeholder="unique-identifier"
                />
                <small className="text-muted">
                  This will be used as a unique identifier for the tenant
                </small>
              </div>
              
              <div className="form-group">
                <label className="form-label">OpenAI API Key</label>
                <input
                  type="password"
                  className="form-control"
                  value={formData.openai_api_key}
                  onChange={(e) => setFormData({ ...formData, openai_api_key: e.target.value })}
                  placeholder="sk-..."
                  required
                />
                <small className="text-muted">
                  OpenAI API key is required for this tenant to function
                </small>
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTenant ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Prompt Editor Modal */}
      {showPromptModal && editingPromptTenant && (
        <div className="modal show">
          <div className="modal-content modal-large" style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h3>Conversation Prompt - {editingPromptTenant.name}</h3>
              <button
                className="btn-close"
                onClick={() => {
                  setShowPromptModal(false);
                  setEditingPromptTenant(null);
                  setPromptText('');
                  setFallbackPromptText('');
      setCategorizationPromptText('');
                  setEnableInlineDocumentLinks(false);
                  setSourceMode('retrieval_based');
                  setSourceMatchMode('fuzzy');
                  setMaxSources(4);
                  setSearchSemanticWeight(0.62);
                  setSearchKeywordWeight(0.25);
                  setSearchTitleWeight(0.13);
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '6px', borderLeft: '3px solid #007bff' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#004085' }}>
                  <strong>💡 OpenAI Prompt Caching:</strong> Prompts over 1024 tokens are automatically cached, reducing costs by 50% and latency by 80%.
                </p>
              </div>

              <div className="form-group">
                <label className="form-label" style={{ fontWeight: '500', marginBottom: '8px' }}>
                  System Prompt (used with OpenAI Responses API)
                </label>
                <textarea
                  className="form-control"
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  rows={16}
                  placeholder="Enter conversation system prompt..."
                  style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: '13px', lineHeight: '1.6' }}
                />
                <small className="text-muted">
                  {promptText.length} characters
                  {promptText.length > 1024 && <span style={{ color: '#28a745', fontWeight: '500', marginLeft: '10px' }}>✓ Eligible for caching</span>}
                </small>
              </div>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label className="form-label" style={{ fontWeight: '500', marginBottom: '4px' }}>
                  Fallback Instruction <span style={{ fontWeight: 400, color: '#6c757d' }}>(injected when knowledge base has no results)</span>
                </label>
                <div style={{ marginBottom: '8px', padding: '10px 12px', backgroundColor: '#fff8e1', borderRadius: '6px', borderLeft: '3px solid #ffc107' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#856404' }}>
                    When the AI finds <strong>no matching documents</strong>, this instruction tells it how to respond naturally — without inventing facts.
                    Leave empty to use the system default.
                  </p>
                </div>
                <textarea
                  className="form-control"
                  value={fallbackPromptText}
                  onChange={(e) => setFallbackPromptText(e.target.value)}
                  rows={6}
                  placeholder={`Leave empty to use system default, or customize e.g.:\n\n[NOTĂ SISTEM — LIPSĂ DATE]\nBaza de cunoștințe nu conține informații pentru această întrebare.\nNu inventa fapte. Explică natural că nu ai date verificate și sugerează utilizatorului să contacteze primăria direct sau să viziteze site-ul oficial.`}
                  style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: '13px', lineHeight: '1.6' }}
                />
                <small className="text-muted">
                  {fallbackPromptText.length > 0
                    ? `${fallbackPromptText.length} characters — overrides system default`
                    : 'Empty — system default active'}
                </small>
              </div>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label className="form-label" style={{ fontWeight: '500', marginBottom: '4px' }}>
                  Categorization Rules <span style={{ fontWeight: 400, color: '#6c757d' }}>(defines what each category means for this tenant)</span>
                </label>
                <div style={{ marginBottom: '8px', padding: '10px 12px', backgroundColor: '#f3e8ff', borderRadius: '6px', borderLeft: '3px solid #7C3AED' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#5b21b6' }}>
                    Used by the nightly AI job to categorize conversations. Write domain-specific rules for each category.
                    Must include <code style={{ background: '#ede9fe', padding: '0 3px', borderRadius: '3px' }}>{'{conversation}'}</code> placeholder.
                    Leave empty to use the system default.
                  </p>
                </div>
                <textarea
                  className="form-control"
                  value={categorizationPromptText}
                  onChange={(e) => setCategorizationPromptText(e.target.value)}
                  rows={10}
                  placeholder={`Leave empty to use system default, or customize e.g.:\n\nCategorize this conversation into exactly ONE category.\n\nConversation:\n{conversation}\n\nCategory definitions:\n- Offer/Price: user asks for a price or quote with dimensions...\n- Technical: questions about materials, specs, mechanisms...\n- Contact/Company: where to find us, who does installation...\n- General Info: overview questions, comparisons, warranty...\n- Other: ONLY for greetings-only or test messages.\n\nReturn JSON: {"category": "<value>"}`}
                  style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: '12px', lineHeight: '1.6' }}
                />
                <small className="text-muted">
                  {categorizationPromptText.length > 0
                    ? `${categorizationPromptText.length} characters — overrides system default`
                    : 'Empty — system default active'}
                </small>
              </div>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label className="form-label" style={{ fontWeight: '500', marginBottom: '8px' }}>
                  Source & Link Behavior (per tenant)
                </label>
                <div style={{ display: 'grid', gap: '12px', background: '#f8f9fa', borderRadius: '8px', padding: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={enableInlineDocumentLinks}
                      onChange={(e) => setEnableInlineDocumentLinks(e.target.checked)}
                    />
                    Enable inline document links in assistant answer (Markdown links)
                  </label>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Source mode</label>
                    <select className="form-control" value={sourceMode} onChange={(e) => setSourceMode(e.target.value)}>
                      <option value="retrieval_based">retrieval_based (default)</option>
                      <option value="response_only">response_only</option>
                      <option value="hybrid">hybrid</option>
                    </select>
                    <small className="text-muted">
                      response_only = include only sources explicitly used/mentioned in final answer.
                    </small>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Source match mode</label>
                    <select className="form-control" value={sourceMatchMode} onChange={(e) => setSourceMatchMode(e.target.value)}>
                      <option value="fuzzy">fuzzy (diacritics/synonyms tolerant)</option>
                      <option value="strict">strict</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Max sources</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      className="form-control"
                      value={maxSources}
                      onChange={(e) => setMaxSources(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label className="form-label" style={{ fontWeight: '500', marginBottom: '8px' }}>
                  Hybrid Search Weights <span style={{ fontWeight: 400, color: '#6c757d' }}>(auto-normalised to sum = 1.0)</span>
                </label>
                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', display: 'grid', gap: '10px' }}>
                  {[
                    { label: 'Semantic (PGVector cosine)', value: searchSemanticWeight, setter: setSearchSemanticWeight },
                    { label: 'Keyword (full-text BM25)', value: searchKeywordWeight, setter: setSearchKeywordWeight },
                    { label: 'Title (page title / filename)', value: searchTitleWeight, setter: setSearchTitleWeight },
                  ].map(({ label, value, setter }) => {
                    const total = Number(searchSemanticWeight) + Number(searchKeywordWeight) + Number(searchTitleWeight);
                    const pct = total > 0 ? Math.round((Number(value) / total) * 100) : 0;
                    return (
                      <div key={label} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', alignItems: 'center' }}>
                        <div>
                          <label style={{ fontSize: '13px', margin: 0 }}>{label}</label>
                          <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.01}
                            value={value}
                            onChange={(e) => setter(parseFloat(e.target.value))}
                            style={{ width: '100%', cursor: 'pointer' }}
                          />
                        </div>
                        <input
                          type="number"
                          min={0}
                          max={1}
                          step={0.01}
                          value={value}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            if (isNaN(v)) { setter(0); return; }
                            setter(Math.min(1, Math.max(0, v)));
                          }}
                          style={{ width: '70px', textAlign: 'right', borderColor: (Number(value) < 0 || Number(value) > 1) ? '#dc3545' : '' }}
                          className="form-control"
                        />
                        <span style={{ fontSize: '12px', color: '#6c757d', width: '36px', textAlign: 'right' }}>{pct}%</span>
                      </div>
                    );
                  })}
                  {(Number(searchSemanticWeight) + Number(searchKeywordWeight) + Number(searchTitleWeight)) === 0 && (
                    <div style={{ padding: '6px 10px', background: '#f8d7da', borderRadius: '4px', color: '#842029', fontSize: '13px' }}>
                      All weights are 0 — at least one must be greater than 0.
                    </div>
                  )}
                  <small className="text-muted" style={{ marginTop: '4px' }}>
                    Each value: 0.00–1.00. Weights are auto-normalised to sum = 1.0 before use. Defaults: 0.62 · 0.25 · 0.13.
                  </small>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowPromptModal(false);
                  setEditingPromptTenant(null);
                  setPromptText('');
                  setFallbackPromptText('');
      setCategorizationPromptText('');
                  setEnableInlineDocumentLinks(false);
                  setSourceMode('retrieval_based');
                  setSourceMatchMode('fuzzy');
                  setMaxSources(4);
                  setSearchSemanticWeight(0.62);
                  setSearchKeywordWeight(0.25);
                  setSearchTitleWeight(0.13);
                }}
                disabled={savingPrompt}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSavePrompt}
                disabled={savingPrompt}
              >
                {savingPrompt ? 'Saving...' : 'Save Prompt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManager; 
